import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import {
    InboxIcon,
    MessageIcon,
    NotificationIcon as Bell,
    SendIcon,
    SearchIcon,
    ArrowLeftIcon,
    CheckCircleIcon,
    DeleteIcon,
    LoaderIcon,
    CalendarIcon,
    BlogIcon,
    AlertCircleIcon,
    ClockIcon,
    MailIcon,
    PlusIcon as AddIcon,
    ChevronRightIcon,
    SparklesIcon
} from '../components/Icons';

const TYPE_CONFIG = {
    contact_request: { label: 'Anfrage', icon: MessageIcon, color: 'bg-amber-500/10 text-amber-500' },
    notification: { label: 'System', icon: Bell, color: 'bg-primary/10 text-primary' },
    email_incoming: { label: 'E-Mail', icon: InboxIcon, color: 'bg-secondary/10 text-secondary' },
    email_outgoing: { label: 'Gesendet', icon: SendIcon, color: 'bg-tertiary/10 text-tertiary' },
    event: { label: 'Event', icon: CalendarIcon, color: 'bg-tertiary/10 text-tertiary' },
    blog: { label: 'Blog', icon: BlogIcon, color: 'bg-amber-500/10 text-amber-500' }
};

const Mailbox = () => {
    const { user, API_URL } = useContext(AuthContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const [items, setItems] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [folder, setFolder] = useState('inbox'); // inbox, sent, anfragen, system
    const [replying, setReplying] = useState(false);
    const [replyText, setReplyText] = useState('');
    const [replySubject, setReplySubject] = useState('');
    const [sendingReply, setSendingReply] = useState(false);
    const [message, setMessage] = useState(null);

    // New Message state
    const [isComposing, setIsComposing] = useState(false);
    const [composeTo, setComposeTo] = useState('');

    // Bob context states
    const [infoInput, setInfoInput] = useState('');
    const [isSavingContext, setIsSavingContext] = useState(false);
    const [contextSaved, setContextSaved] = useState(false);

    const config = { headers: { Authorization: `Bearer ${user?.token}` } };

    const handleSaveBobContext = async (e) => {
        e.preventDefault();
        if (!infoInput.trim()) return;
        setIsSavingContext(true);
        try {
            let existingKnowledge = [];
            try {
                const res = await axios.get(`${API_URL}/settings/bob_knowledge`, config);
                if (Array.isArray(res.data)) {
                    existingKnowledge = res.data;
                }
            } catch (err) {
                // 404 is expected if key doesn't exist yet
            }

            const newEntry = {
                id: crypto.randomUUID(),
                text: infoInput,
                createdAt: new Date().toISOString()
            };

            await axios.put(`${API_URL}/settings/bob_knowledge`, [...existingKnowledge, newEntry], config);
            setContextSaved(true);
            setMessage({ text: 'Information erfolgreich in Bobs Kontext aufgenommen!', type: 'success' });
        } catch (err) {
            console.error('Failed to save bob knowledge context:', err);
            setMessage({ text: 'Fehler beim Speichern der Information.', type: 'error' });
        } finally {
            setIsSavingContext(false);
            setTimeout(() => setMessage(null), 3000);
        }
    };

    const fetchAll = useCallback(async () => {
        if (!user?.token) return;
        setLoading(true);
        try {
            const [contactsRes, notifsRes, emailsIncRes, emailsOutRes] = await Promise.all([
                axios.get(`${API_URL}/contact`, config).catch(err => {
                    console.warn('Mailbox: Contact requests could not be loaded:', err.message);
                    return { data: [] };
                }),
                axios.get(`${API_URL}/notifications/inbox?limit=50`, config).catch(err => {
                    console.warn('Mailbox: Notifications could not be loaded:', err.message);
                    return { data: { notifications: [] } };
                }),
                axios.get(`${API_URL}/emails?type=incoming`, config).catch(err => {
                    return { data: [] };
                }),
                axios.get(`${API_URL}/emails?type=outgoing`, config).catch(err => {
                    return { data: [] };
                })
            ]);

            const contacts = (contactsRes.data || []).map(c => ({
                ...c,
                _mailboxType: 'contact_request',
                title: c.name,
                subtitle: c.subject,
                body: c.message,
                isRead: c.isResolved, 
                createdAt: c.createdAt
            }));

            const notifs = (notifsRes.data.notifications || []).map(n => ({
                ...n,
                _mailboxType: 'notification',
                subtitle: n.title,
                title: 'System',
                body: n.message,
                createdAt: n.createdAt
            }));

            const emailsInc = (emailsIncRes.data || []).map(e => ({
                ...e,
                _mailboxType: 'email_incoming',
                title: e.sender,
                subtitle: e.subject,
                body: e.body,
                isRead: e.isRead === 1,
                createdAt: e.createdAt
            }));

            const emailsOut = (emailsOutRes.data || []).map(e => ({
                ...e,
                _mailboxType: 'email_outgoing',
                title: e.recipient,
                subtitle: e.subject,
                body: e.body,
                isRead: true,
                createdAt: e.createdAt
            }));

            const combined = [...contacts, ...notifs, ...emailsInc, ...emailsOut].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            setItems(combined);

            const selectId = searchParams.get('id');
            if (selectId) {
                const found = combined.find(i => i._id === selectId);
                if (found) {
                    setSelectedItem(found);
                    if (found._mailboxType === 'contact_request') setFolder('anfragen');
                    else if (found._mailboxType === 'notification') setFolder('system');
                    else if (found._mailboxType === 'email_incoming') setFolder('inbox');
                    else if (found._mailboxType === 'email_outgoing') setFolder('sent');
                }
            } else {
                // Auto-switch to folder with unread messages if inbox has none
                const hasUnreadInbox = emailsInc.some(e => !e.isRead);
                const hasUnreadContacts = contacts.some(c => !c.isRead);
                const hasUnreadNotifs = notifs.some(n => !n.isRead);
                if (!hasUnreadInbox) {
                    if (hasUnreadContacts) setFolder('anfragen');
                    else if (hasUnreadNotifs) setFolder('system');
                }
            }
        } catch (err) {
            console.error('Mailbox fetch error:', err);
        } finally {
            setLoading(false);
        }
    }, [user, API_URL]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const handleSelect = (item) => {
        setSelectedItem(item);
        setIsComposing(false);
        setReplying(false);
        setReplyText('');
        setInfoInput(item.action?.suggestedInfo || '');
        setContextSaved(false);
        if (item._mailboxType === 'notification' && !item.isRead) {
            axios.put(`${API_URL}/notifications/${item._id}/read`, {}, config).catch(() => {});
            setItems(prev => prev.map(i => i._id === item._id ? { ...i, isRead: true } : i));
        } else if (item._mailboxType === 'email_incoming' && !item.isRead) {
            axios.put(`${API_URL}/emails/${item._id}/read`, {}, config).catch(() => {});
            setItems(prev => prev.map(i => i._id === item._id ? { ...i, isRead: true } : i));
        }
    };

    const handleReply = async (e) => {
        e.preventDefault();
        if (!selectedItem || !replyText.trim()) return;
        setSendingReply(true);
        try {
            const isContact = selectedItem._mailboxType === 'contact_request';
            const endpoint = isContact ? `${API_URL}/contact/${selectedItem._id}/reply` : `${API_URL}/emails/send`;
            const payload = isContact ? {
                subject: replySubject || `AW: ${selectedItem.subtitle}`,
                message: replyText
            } : {
                to: selectedItem.sender,
                subject: replySubject || `Aw: ${selectedItem.subtitle}`,
                message: replyText
            };

            await axios.post(endpoint, payload, config);
            
            setMessage({ text: 'Antwort gesendet!', type: 'success' });
            setReplying(false);
            setReplyText('');
            fetchAll();
        } catch (err) {
            const errorMsg = err.response?.data?.message || 'Fehler beim Senden.';
            setMessage({ text: errorMsg, type: 'error' });
        } finally {
            setSendingReply(false);
            setTimeout(() => setMessage(null), 3000);
        }
    };

    const handleSendNew = async (e) => {
        e.preventDefault();
        if (!composeTo.trim() || !replySubject.trim() || !replyText.trim()) return;
        setSendingReply(true);
        try {
            await axios.post(`${API_URL}/emails/send`, {
                to: composeTo,
                subject: replySubject,
                message: replyText
            }, config);
            
            setMessage({ text: 'E-Mail erfolgreich gesendet!', type: 'success' });
            setIsComposing(false);
            setReplyText('');
            setReplySubject('');
            setComposeTo('');
            fetchAll();
        } catch (err) {
            const errorMsg = err.response?.data?.message || 'Fehler beim Senden.';
            setMessage({ text: errorMsg, type: 'error' });
        } finally {
            setSendingReply(false);
            setTimeout(() => setMessage(null), 3000);
        }
    };

    const toggleResolved = async (item) => {
        try {
            await axios.put(`${API_URL}/contact/${item._id}`, { isResolved: !item.isResolved }, config);
            setItems(prev => prev.map(i => i._id === item._id ? { ...i, isResolved: !item.isResolved, isRead: !item.isResolved } : i));
            if (selectedItem?._id === item._id) {
                setSelectedItem(prev => ({ ...prev, isResolved: !item.isResolved, isRead: !item.isResolved }));
            }
        } catch (err) { /* silent */ }
    };

    const handleDelete = async (item) => {
        if (!item) return;
        
        let confirmText = 'Möchten Sie diese Nachricht wirklich löschen?';
        if (item._mailboxType === 'contact_request') {
            confirmText = 'Möchten Sie diese Kontaktanfrage wirklich unwiderruflich löschen?';
        } else if (item._mailboxType === 'notification') {
            confirmText = 'Möchten Sie diese Systembenachrichtigung wirklich unwiderruflich löschen?';
        } else {
            setMessage({ text: 'Löschen von E-Mails wird aktuell nicht unterstützt.', type: 'error' });
            setTimeout(() => setMessage(null), 3000);
            return;
        }

        if (!window.confirm(confirmText)) return;

        try {
            if (item._mailboxType === 'contact_request') {
                await axios.delete(`${API_URL}/contact/${item._id}`, config);
            } else if (item._mailboxType === 'notification') {
                await axios.delete(`${API_URL}/notifications/${item._id}`, config);
            }

            setMessage({ text: 'Nachricht erfolgreich gelöscht.', type: 'success' });
            setItems(prev => prev.filter(i => i._id !== item._id));
            setSelectedItem(null);
        } catch (err) {
            console.error('Failed to delete mailbox item:', err);
            setMessage({ text: 'Fehler beim Löschen der Nachricht.', type: 'error' });
        } finally {
            setTimeout(() => setMessage(null), 3000);
        }
    };


    const filteredItems = items.filter(item => {
        const matchesSearch = (item.title + item.subtitle + item.body).toLowerCase().includes(search.toLowerCase());
        let matchesFolder = false;
        if (folder === 'inbox') matchesFolder = item._mailboxType === 'email_incoming';
        else if (folder === 'sent') matchesFolder = item._mailboxType === 'email_outgoing';
        else if (folder === 'anfragen') matchesFolder = item._mailboxType === 'contact_request';
        else if (folder === 'system') matchesFolder = item._mailboxType === 'notification';
        
        return matchesSearch && matchesFolder;
    });

    const timeAgo = (date) => {
        if (!date) return '';
        const seconds = Math.floor((new Date() - new Date(date)) / 1000);
        if (seconds < 60) return 'Gerade eben';
        if (seconds < 3600) return Math.floor(seconds / 60) + 'm';
        if (seconds < 86400) return Math.floor(seconds / 3600) + 'h';
        return new Date(date).toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });
    };

    return (
        <div className="h-[calc(100vh-100px)] md:h-[calc(100vh-32px)] flex flex-col md:flex-row bg-surface animate-fade-in overflow-hidden mx-4 md:mx-6 mt-4 md:mt-2 mb-4 rounded-3xl border border-outlineVariant/20 shadow-elevation-1">
            {/* Sidebar List */}
            <div className={`w-full md:w-80 lg:w-[400px] border-r border-outlineVariant/30 flex flex-col bg-surface-containerLow shrink-0 ${ (selectedItem || isComposing) ? 'hidden md:flex' : 'flex'}`}>
                <div className="p-6 border-b border-outlineVariant/30">
                    <div className="flex items-center justify-between mb-6">
                        <h1 className="text-display-small font-black text-surface-on tracking-tighter">Postfach</h1>
                        <div className="flex items-center gap-1">
                            <button onClick={() => { setIsComposing(true); setSelectedItem(null); setReplySubject(''); setReplyText(''); setComposeTo(''); }} className="w-10 h-10 rounded-full bg-primary text-primary-on flex items-center justify-center shadow-sm hover:scale-105 transition-transform"><AddIcon style={{ fontSize: 20 }} /></button>
                            <button onClick={fetchAll} className="w-10 h-10 rounded-full hover:bg-surface-on/10 flex items-center justify-center transition-colors"><ClockIcon style={{ fontSize: 20 }} /></button>
                        </div>
                    </div>
                    
                    <div className="relative mb-6">
                        <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-surface-onVariant/50" style={{ fontSize: 20 }} />
                        <input 
                            value={search} 
                            onChange={e => setSearch(e.target.value)} 
                            placeholder="Suchen..." 
                            className="w-full bg-surface-containerHighest text-surface-on rounded-2xl pl-12 pr-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all font-medium"
                        />
                    </div>

                    <div className="grid grid-cols-4 gap-1.5">
                        {[
                            { id: 'inbox', label: 'E-Mails', count: items.filter(i => i._mailboxType === 'email_incoming' && !i.isRead).length },
                            { id: 'sent', label: 'Gesendet', count: 0 },
                            { id: 'anfragen', label: 'Anfragen', count: items.filter(i => i._mailboxType === 'contact_request' && !i.isRead).length },
                            { id: 'system', label: 'System', count: items.filter(i => i._mailboxType === 'notification' && !i.isRead).length }
                        ].map(f => (
                            <button 
                                key={f.id}
                                onClick={() => setFolder(f.id)}
                                className={`py-3 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all flex items-center justify-center gap-1 ${folder === f.id ? 'bg-primary text-primary-on shadow-sm' : 'bg-surface-containerHighest text-surface-onVariant hover:bg-surface-on/10'}`}
                            >
                                <span className="truncate">{f.label}</span>
                                {f.count > 0 && (
                                    <span className={`px-1 py-0.25 rounded-full text-[8px] font-bold shrink-0 ${folder === f.id ? 'bg-white/20 text-white' : 'bg-primary text-primary-on'}`}>
                                        {f.count}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20 gap-4">
                            <LoaderIcon className="animate-spin text-primary" style={{ fontSize: 32 }} />
                            <p className="text-xs font-bold uppercase tracking-widest opacity-40">Lade Postfach...</p>
                        </div>
                    ) : filteredItems.length === 0 ? (
                        <div className="text-center py-20 px-8">
                            <MailIcon style={{ fontSize: 64 }} className="mx-auto mb-6 opacity-5" />
                            <p className="text-surface-onVariant font-bold tracking-tight text-lg">Keine Nachrichten</p>
                            <p className="text-sm text-surface-onVariant opacity-60 mt-2">Filtere anders oder suche neu</p>
                        </div>
                    ) : (
                        filteredItems.map(item => {
                            const config = TYPE_CONFIG[item._mailboxType] || TYPE_CONFIG.notification;
                            const Icon = config.icon;
                            return (
                                <button 
                                    key={item._id}
                                    onClick={() => handleSelect(item)}
                                    className={`w-full text-left px-6 py-5 border-b border-outlineVariant/10 transition-all flex gap-4 group relative ${selectedItem?._id === item._id ? 'bg-primary/5' : 'hover:bg-surface-on/[0.03]'}`}
                                >
                                    {!item.isRead && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-10 bg-primary rounded-r-full" />}
                                    <div className={`w-12 h-12 rounded-2xl shrink-0 flex items-center justify-center shadow-sm ${config.color}`}>
                                        <Icon style={{ fontSize: 24 }} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-start mb-1">
                                            <span className={`text-[15px] truncate ${!item.isRead ? 'font-black text-surface-on' : 'font-bold text-surface-onVariant'}`}>{item.title}</span>
                                            <span className="text-[10px] font-black uppercase tracking-tighter text-surface-onVariant opacity-40 shrink-0">{timeAgo(item.createdAt)}</span>
                                        </div>
                                        <p className={`text-sm truncate mb-1 ${!item.isRead ? 'font-bold text-surface-on' : 'text-surface-onVariant opacity-80'}`}>{item.subtitle}</p>
                                        <p className="text-xs text-surface-onVariant/60 truncate leading-normal">{item.body?.substring(0, 100)}</p>
                                    </div>
                                </button>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Reading Pane / Compose View */}
            <div className={`flex-1 flex flex-col bg-surface overflow-hidden ${(!selectedItem && !isComposing) ? 'hidden md:flex' : 'flex'}`}>
                {isComposing ? (
                    /* Compose View */
                    <div className="flex-1 flex flex-col animate-fade-in">
                        <div className="px-8 py-6 border-b border-outlineVariant/30 flex items-center justify-between bg-surface-containerLowest/30">
                            <div className="flex items-center gap-4">
                                <button onClick={() => setIsComposing(false)} className="md3-icon-btn"><ArrowLeftIcon /></button>
                                <h2 className="text-title-large font-black text-surface-on tracking-tight">Neue Nachricht schreiben</h2>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                            <form onSubmit={handleSendNew} className="max-w-4xl mx-auto space-y-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-black uppercase tracking-[0.2em] text-surface-onVariant px-1">Empfänger (E-Mail)</label>
                                    <input 
                                        value={composeTo}
                                        onChange={e => setComposeTo(e.target.value)}
                                        placeholder="beispiel@mail.de"
                                        className="w-full bg-surface-containerLow text-surface-on p-5 rounded-3xl border-2 border-transparent focus:border-primary/30 outline-none transition-all font-bold"
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-black uppercase tracking-[0.2em] text-surface-onVariant px-1">Betreff</label>
                                    <input 
                                        value={replySubject}
                                        onChange={e => setReplySubject(e.target.value)}
                                        placeholder="Betreff Ihrer Nachricht"
                                        className="w-full bg-surface-containerLow text-surface-on p-5 rounded-3xl border-2 border-transparent focus:border-primary/30 outline-none transition-all font-bold"
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-black uppercase tracking-[0.2em] text-surface-onVariant px-1">Inhalt</label>
                                    <textarea 
                                        value={replyText}
                                        onChange={e => setReplyText(e.target.value)}
                                        placeholder="Ihre Nachricht..."
                                        rows={12}
                                        className="w-full bg-surface-containerLow text-surface-on p-5 rounded-3xl border-2 border-transparent focus:border-primary/30 outline-none transition-all resize-none font-medium leading-relaxed"
                                        required
                                    />
                                </div>
                                <div className="flex justify-end gap-4 pt-4">
                                    <button type="button" onClick={() => setIsComposing(false)} className="px-8 h-14 rounded-2xl font-bold hover:bg-surface-on/5 transition-colors">Abbrechen</button>
                                    <button 
                                        type="submit" 
                                        disabled={sendingReply || !replyText.trim()} 
                                        className="px-10 h-14 bg-primary text-primary-on rounded-2xl font-black uppercase tracking-widest shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50 flex items-center gap-3"
                                    >
                                        {sendingReply ? <LoaderIcon className="animate-spin" /> : <><SendIcon style={{ fontSize: 20 }} /> Senden</>}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                ) : selectedItem ? (
                    /* Reading Pane */
                    <>
                        {/* Header */}
                        <div className="px-8 py-6 border-b border-outlineVariant/30 flex items-center justify-between bg-surface-containerLowest/30">
                            <div className="flex items-center gap-6">
                                <button onClick={() => setSelectedItem(null)} className="md:hidden md3-icon-btn"><ArrowLeftIcon /></button>
                                <div className={`w-16 h-16 rounded-[1.25rem] shrink-0 flex items-center justify-center shadow-sm ${TYPE_CONFIG[selectedItem._mailboxType]?.color || 'bg-surface-variant'}`}>
                                    {React.createElement(TYPE_CONFIG[selectedItem._mailboxType]?.icon || MailIcon, { style: { fontSize: 32 } })}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <h2 className="text-headline-small font-black text-surface-on tracking-tighter leading-tight mb-2 truncate">{selectedItem.subtitle}</h2>
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                        <span className="text-sm font-bold text-primary truncate max-w-[200px]">{selectedItem.title}</span>
                                        <span className="text-xs text-surface-onVariant opacity-40 font-black">•</span>
                                        <span className="text-xs font-black uppercase tracking-tighter text-surface-onVariant opacity-60">{new Date(selectedItem.createdAt).toLocaleString('de-DE')}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                {selectedItem._mailboxType === 'contact_request' && (
                                    <button 
                                        onClick={() => toggleResolved(selectedItem)}
                                        className={`px-5 h-11 rounded-full font-black text-[10px] uppercase tracking-widest transition-all flex items-center gap-2 border-2 ${selectedItem.isResolved ? 'bg-green-500/10 text-green-600 border-green-500/20' : 'bg-surface-on/5 text-surface-on border-transparent hover:border-surface-on/20'}`}
                                    >
                                        <CheckCircleIcon style={{ fontSize: 16 }} />
                                        {selectedItem.isResolved ? 'Erledigt' : 'Offen'}
                                    </button>
                                )}
                                <button onClick={() => handleDelete(selectedItem)} className="w-11 h-11 rounded-full hover:bg-error/10 hover:text-error flex items-center justify-center transition-all opacity-40 hover:opacity-100"><DeleteIcon style={{ fontSize: 22 }} /></button>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-8 md:p-12 custom-scrollbar">
                            <div className="max-w-4xl mx-auto">
                                <div className="bg-surface-containerLow p-10 rounded-[2.5rem] border border-outlineVariant/20 shadow-sm mb-10">
                                    {selectedItem.html ? (
                                        <div 
                                            className="email-content text-surface-on prose max-w-none"
                                            dangerouslySetInnerHTML={{ __html: selectedItem.html }}
                                        />
                                    ) : (
                                        <p className="text-lg md:text-xl text-surface-on leading-relaxed whitespace-pre-wrap font-medium">
                                            {selectedItem.body}
                                        </p>
                                    )}
                                </div>

                                {selectedItem._mailboxType === 'notification' && selectedItem.subtitle === 'Nachricht von Bob (Feedback/Info)' && (
                                    <div className="bg-surface-containerHigh p-8 rounded-[2.5rem] border-4 border-primary/10 mb-10 animate-slide-up">
                                        <h3 className="text-headline-small font-black tracking-tight flex items-center gap-3 mb-4">
                                            <SparklesIcon className="text-primary" /> Bobs Kontext aktualisieren
                                        </h3>
                                        {contextSaved ? (
                                            <div className="bg-green-500/10 text-green-600 p-6 rounded-2xl font-bold">
                                                Information wurde erfolgreich in Bobs Kontext aufgenommen! Sie wird bei zukünftigen Anfragen berücksichtigt.
                                            </div>
                                        ) : (
                                            <form onSubmit={handleSaveBobContext} className="space-y-6">
                                                <p className="text-body-medium text-surface-onVariant">
                                                    Gib hier die fehlenden oder korrigierten Informationen ein. Diese werden direkt in Bobs Wissensdatenbank gespeichert.
                                                </p>
                                                <textarea
                                                    value={infoInput}
                                                    onChange={e => setInfoInput(e.target.value)}
                                                    placeholder="Fehlende Details hier eintragen (z. B. Das Sommerlager findet statt...)"
                                                    rows={4}
                                                    className="w-full bg-surface-containerLowest text-surface-on p-5 rounded-2xl border-2 border-transparent focus:border-primary/30 outline-none transition-all resize-none font-bold leading-relaxed"
                                                    required
                                                />
                                                <div className="flex justify-end">
                                                    <button
                                                        type="submit"
                                                        disabled={isSavingContext || !infoInput.trim()}
                                                        className="px-10 h-14 bg-primary text-primary-on rounded-2xl font-black uppercase tracking-widest shadow-md hover:brightness-110 active:scale-95 transition-all"
                                                    >
                                                        {isSavingContext ? 'Speichert...' : 'In Kontext aufnehmen'}
                                                    </button>
                                                </div>
                                            </form>
                                        )}
                                    </div>
                                )}

                                {(selectedItem._mailboxType === 'contact_request' || selectedItem._mailboxType === 'email_incoming') && (
                                    <div className="space-y-8">
                                        {!replying ? (
                                            <button 
                                                onClick={() => { setReplying(true); setReplySubject(`Aw: ${selectedItem.subtitle}`); }}
                                                className="h-16 px-10 bg-primary text-primary-on rounded-2xl font-black uppercase tracking-widest shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-95 transition-all flex items-center gap-4"
                                            >
                                                <SendIcon style={{ fontSize: 24 }} /> Antworten
                                            </button>
                                        ) : (
                                            <div className="bg-surface-containerHigh p-8 rounded-[2.5rem] border-4 border-primary/10 animate-slide-up">
                                                <div className="flex items-center justify-between mb-6">
                                                    <h3 className="text-headline-small font-black tracking-tight flex items-center gap-3"><SendIcon className="text-primary" /> Schnelle Antwort</h3>
                                                    <button onClick={() => setReplying(false)} className="w-10 h-10 rounded-full hover:bg-surface-on/10 flex items-center justify-center transition-colors"><ArrowLeftIcon /></button>
                                                </div>
                                                <form onSubmit={handleReply} className="space-y-6">
                                                    <input 
                                                        value={replySubject}
                                                        onChange={e => setReplySubject(e.target.value)}
                                                        placeholder="Betreff"
                                                        className="w-full bg-surface-containerLowest text-surface-on p-5 rounded-2xl border-2 border-transparent focus:border-primary/30 outline-none transition-all font-black text-sm"
                                                    />
                                                    <textarea 
                                                        value={replyText}
                                                        onChange={e => setReplyText(e.target.value)}
                                                        placeholder="Nachricht schreiben..."
                                                        rows={10}
                                                        className="w-full bg-surface-containerLowest text-surface-on p-5 rounded-2xl border-2 border-transparent focus:border-primary/30 outline-none transition-all resize-none font-bold leading-relaxed"
                                                    />
                                                    <div className="flex justify-end gap-4">
                                                        <button type="button" onClick={() => setReplying(false)} className="px-6 h-12 font-bold opacity-60 hover:opacity-100 transition-opacity">Abbrechen</button>
                                                        <button 
                                                            type="submit" 
                                                            disabled={sendingReply || !replyText.trim()} 
                                                            className="px-10 h-14 bg-primary text-primary-on rounded-2xl font-black uppercase tracking-widest shadow-md hover:brightness-110 active:scale-95 transition-all"
                                                        >
                                                            {sendingReply ? <LoaderIcon className="animate-spin" /> : 'Senden'}
                                                        </button>
                                                    </div>
                                                </form>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                ) : (
                    /* Empty State */
                    <div className="flex-1 flex flex-col items-center justify-center text-center p-12 animate-scale-in">
                        <div className="w-32 h-32 rounded-[3rem] bg-surface-containerHigh flex items-center justify-center mb-10 shadow-elevation-1">
                            <MailIcon style={{ fontSize: 64 }} className="text-primary opacity-20" />
                        </div>
                        <h2 className="text-display-medium font-black text-surface-on mb-4 tracking-tighter">Postfach auswählen</h2>
                        <p className="text-xl text-surface-onVariant max-w-sm font-bold opacity-60 leading-relaxed">Wählen Sie eine Nachricht aus der Liste aus oder schreiben Sie eine neue.</p>
                        <button 
                            onClick={() => setIsComposing(true)}
                            className="mt-10 px-8 h-14 md3-btn-tonal gap-3 font-black uppercase tracking-widest"
                        >
                            <AddIcon /> Neue Nachricht
                        </button>
                    </div>
                )}
            </div>

            {/* Toast Message */}
            {message && (
                <div className={`fixed bottom-12 left-1/2 -translate-x-1/2 px-8 py-4 rounded-3xl shadow-elevation-5 animate-slide-up flex items-center gap-4 z-50 ${message.type === 'error' ? 'bg-error text-error-on' : 'bg-primary text-primary-on'}`}>
                    {message.type === 'error' ? <AlertCircleIcon style={{ fontSize: 24 }} /> : <CheckCircleIcon style={{ fontSize: 24 }} />}
                    <span className="font-black uppercase tracking-wider text-sm">{message.text}</span>
                </div>
            )}
        </div>
    );
};

export default Mailbox;
